import { signal, effect, computed } from '@preact/signals';
import { track } from './telemetry.js';
import { secureStorage } from '../storage/secureStorage.js';

/**
 * Per-bible reading progress.
 *
 * `heard` is the set of chapter keys (`BIBLE:BOOK.CHAPTER`) the user has
 * listened to end-to-end. The controller calls `markChapterHeard` on
 * the audio element's `ended` event (and on synth chapter-end in demo).
 *
 * `dailyLog` is a date-keyed map of chapters heard that day. Used to
 * compute the streak — "days in a row with at least one new chapter
 * heard." Local-time YYYY-MM-DD keys; the streak resets if a day is
 * skipped.
 */

const HEARD_KEY = '_hd1';
const LOG_KEY = '_dl1';
const MAX_LOG_DAYS = 365;

type HeardSet = Record<string, true>;
type DailyLog = Record<string, string[]>; // ISO date → chapter keys heard

function loadHeard(): HeardSet {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(HEARD_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function loadLog(): DailyLog {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(LOG_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export const heardChapters = signal<HeardSet>(loadHeard());
export const dailyLog = signal<DailyLog>(loadLog());

if (typeof window !== 'undefined') {
  effect(() => {
    void secureStorage.setItem(HEARD_KEY, JSON.stringify(heardChapters.value));
  });
  effect(() => {
    void secureStorage.setItem(LOG_KEY, JSON.stringify(dailyLog.value));
  });
}

export function chapterKey(bibleId: string, book: string, chapter: number): string {
  return `${bibleId.toUpperCase()}:${book.toUpperCase()}.${chapter}`;
}

export function isHeard(bibleId: string, book: string, chapter: number): boolean {
  return heardChapters.value[chapterKey(bibleId, book, chapter)] === true;
}

export function markChapterHeard(bibleId: string, book: string, chapter: number): void {
  const key = chapterKey(bibleId, book, chapter);
  if (heardChapters.peek()[key]) {
    // Already known — but still log today's listen for streak purposes
    // even on a repeat, so the user gets streak credit for re-listening.
    appendToToday(key);
    return;
  }
  heardChapters.value = { ...heardChapters.peek(), [key]: true };
  appendToToday(key);
  track('chapter_heard', { bibleId, book, chapter });
}

function appendToToday(chapterKey: string): void {
  const day = todayKey();
  const log = dailyLog.peek();
  const todays = log[day] ?? [];
  if (todays.includes(chapterKey)) return;
  const next: DailyLog = { ...log, [day]: [...todays, chapterKey] };
  // LRU-trim by date so the log can't grow unbounded.
  const days = Object.keys(next).sort();
  if (days.length > MAX_LOG_DAYS) {
    const keep = days.slice(-MAX_LOG_DAYS);
    const trimmed: DailyLog = {};
    for (const d of keep) trimmed[d] = next[d] ?? [];
    dailyLog.value = trimmed;
  } else {
    dailyLog.value = next;
  }
}

export function todayKey(): string {
  return isoDay(new Date());
}

function isoDay(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const TOKENS_KEY = '_ft1';
const FROZEN_DAYS_KEY = '_fd1';

function loadTokens(): number {
  if (typeof localStorage === 'undefined') return 0;
  try {
    const raw = localStorage.getItem(TOKENS_KEY);
    if (!raw) return 0;
    const parsed = JSON.parse(raw);
    return typeof parsed === 'number' ? parsed : 0;
  } catch {
    return 0;
  }
}

function loadFrozenDays(): string[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(FROZEN_DAYS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as unknown[]).filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

export const freezeTokens = signal<number>(loadTokens());
export const frozenDays = signal<string[]>(loadFrozenDays());

if (typeof window !== 'undefined') {
  effect(() => {
    void secureStorage.setItem(TOKENS_KEY, JSON.stringify(freezeTokens.value));
  });
  effect(() => {
    void secureStorage.setItem(FROZEN_DAYS_KEY, JSON.stringify(frozenDays.value));
  });
}

export function consumeFreezeToken(): boolean {
  if (freezeTokens.peek() <= 0) return false;
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const key = isoDay(yesterday);
  const frozen = frozenDays.peek();
  if (frozen.includes(key)) return false;
  const log = dailyLog.peek();
  if ((log[key]?.length ?? 0) > 0) return false;
  frozenDays.value = [...frozen, key];
  freezeTokens.value = freezeTokens.peek() - 1;
  return true;
}

export function addFreezeToken(): void {
  const current = freezeTokens.peek();
  if (current < 5) freezeTokens.value = current + 1;
}

/** Days-in-a-row ending today with at least one chapter logged (frozen days count as heard). */
export const currentStreak = computed<number>(() => {
  const log = dailyLog.value;
  const frozen = new Set(frozenDays.value);
  let streak = 0;
  const cursor = new Date();
  while (true) {
    const key = isoDay(cursor);
    if ((log[key]?.length ?? 0) > 0 || frozen.has(key)) {
      streak += 1;
      cursor.setDate(cursor.getDate() - 1);
    } else {
      break;
    }
  }
  return streak;
});

export const heardCountFor = (bibleId: string): number => {
  const prefix = `${bibleId.toUpperCase()}:`;
  let count = 0;
  for (const key of Object.keys(heardChapters.value)) {
    if (key.startsWith(prefix)) count += 1;
  }
  return count;
};
